/**
 * use-document-types — TanStack Query hook dla listy aktywnych typów dokumentów
 * per klasa. Używany w formularzach tworzenia dokumentów (selektor typu przy create).
 *
 * Zasada: zwraca TYLKO wybieralne typy (selectable === true) — scalona lista może
 * zawierać wiersze globalne wycofane/przesłonięte/zdezaktywowane w firmie, których
 * nie wolno wybrać przy tworzeniu dokumentu (patrz `MergedDocumentTypeRow.selectable`
 * w `@saas/shared`). Pełny katalog odpowiedzi (także niewybieralne) wystawia osobno `allRows`.
 * QueryKey: ['document-types', docClass] — ten sam prefiks co DocumentTypesTab,
 * więc invalidateQueries({ queryKey: ['document-types', docClass] }) po mutacji
 * w ustawieniach automatycznie odświeża selektor.
 */

import type { DocumentClass, MergedDocumentTypeRow } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export type { DocumentClass } from '@saas/shared';

export type DocumentType = MergedDocumentTypeRow;

interface DocumentTypesResponse {
  documentTypes: DocumentType[];
}

export interface UseDocumentTypesOptions {
  /** Gdy podane — zawęża listę do typów danej firmy (`&companyId=...`). */
  companyId?: string | null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useDocumentTypes(
  docClass: DocumentClass,
  options?: UseDocumentTypesOptions,
): {
  data: DocumentType[];
  /**
   * WSZYSTKIE wiersze odpowiedzi, także niewybieralne (przesłonięte przez lokalną kopię,
   * wycofane, zdezaktywowane w firmie) — wyłącznie do mapowania id typu globalnego na jego kod
   * (zawężenie listy po typie: `narrowDocumentTypes`). NIE do wyboru typu — do tego służy `data`.
   */
  allRows: DocumentType[];
  isLoading: boolean;
  isError: boolean;
} {
  const companyId = options?.companyId ?? null;

  const query = useQuery({
    queryKey: ['document-types', docClass, companyId],
    queryFn: async () => {
      let url = `/document-types?class=${encodeURIComponent(docClass)}`;
      if (companyId) {
        url += `&companyId=${encodeURIComponent(companyId)}`;
      }
      const res = await api.get<DocumentTypesResponse>(url);
      return res.data.documentTypes;
    },
    staleTime: 60_000,
  });

  const allRows = query.data ?? [];

  return {
    data: allRows.filter((t) => t.selectable),
    allRows,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
